import { PasswordInput } from "components/PasswordInput/PasswordInput";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import ArrowLeft from "images/arrow_left.svg?react";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, useRef, useState } from "react";
import { Link, Location, useLocation, useNavigate } from "react-router-dom";
import { logIn, signInMfa } from "services/users/auth";
import { ApiRequestError } from "services/errors";
import { getErrorComponents, getSuccessComponents } from "services/util";

import TextField from "@material-ui/core/TextField";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import assert from "utils/assert";
import { SignInLocationState } from "./SignIn";
import styles from "./SignIn.module.scss";
import useSignInForm from "./utils";
import { trackEvent } from "services/productAnalytics";

type Errors = string | ApiRequestError;

type Props = {
  setMfaMode: (mode: boolean) => void;
};

function getCustomErrorComponent(nestedComponent: React.ReactNode) {
  return <div className="talaera-error">{nestedComponent}</div>;
}

export default function EmailSignInForm({ setMfaMode }: Props) {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useRequiredContext(UserContext);
  const { t } = useTranslation();
  const location: Location<SignInLocationState> = useLocation();

  const [errors, setErrors] = useState<Errors | undefined>(location.state?.error);
  const [resetDone, setResetDone] = useState(location.state?.resetDone ?? false);

  const [mfaToken, setMfaToken] = useState(location.state?.mfaToken);

  const initialData = {
    email: location.state?.email ?? "",
    password: mfaToken ? undefined : "",
    code: "",
  };

  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm(initialData, !!mfaToken);

  const codeRef = useRef<HTMLInputElement>(null);

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (mfaToken) {
      handleMfaSubmit();
    } else {
      handleSignIn();
    }
  };

  const handleSignIn = async () => {
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (isValidated) {
      setResetDone(false);
      setErrors("");
      assert(account.email && account.password, "Expected email and password to be set");
      trackEvent("email_sign_in_submitted");
      try {
        const resp = await logIn({
          email: account.email,
          passwd: account.password,
        });

        if (resp.mfa) {
          setMfaToken(resp.token);
          setMfaMode(true);
          codeRef.current?.focus();
        } else {
          await onSignInSuccess();
        }
      } catch (error: any) {
        trackEvent("sign_in_error", { error });
        const firstError =
          error instanceof ApiRequestError ? error.errors?.[0] : undefined;
        if (firstError?.error_code === "sso_required") {
          // SSO-managed account without a password: the SSO sign-in is the only working path.
          const query = new URLSearchParams({ email: account.email ?? "" });
          navigate(`${routes.public.signInSso.path()}?${query}`, {
            replace: true,
          });
          return;
        }
        setErrors(error);
      }
    }
  };

  const handleMfaSubmit = async () => {
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
      code: true,
    });

    if (isValidated) {
      setErrors("");
      try {
        await signInMfa({
          email: account.email as string,
          token: mfaToken as string,
          code: account.code as string,
        });
        await onSignInSuccess();
      } catch (error: any) {
        trackEvent("sign_in_error", { error });
        setErrors(error);
      }
    }
  };

  const onSignInSuccess = async () => {
    await refreshCurrentUser();

    trackEvent("sign_in_success");

    const redirectPath = location.state?.returnPath
      ? location.state.returnPath
      : routes.root.path() + (location.hash ?? "");

    navigate(redirectPath, {
      replace: true,
    });
  };

  const handleBack = () => {
    setMfaMode(false);
    setErrors("");
    setMfaToken(undefined);
  };

  let errorComp;
  if (errors) {
    if (typeof errors === "string") {
      errorComp = getErrorComponents(errors);
    } else if (errors.response?.status === 410) {
      errorComp = getCustomErrorComponent(
        <Trans
          i18nKey="Signin.errors.accountDeleted"
          defaults="Account has been deleted. Please contact <a>customer support</a>"
          components={{
            a: <a key="0" href="mailto:scheduling@talaera.com" />,
          }}
        />
      );
    } else {
      errorComp = getErrorComponents(errors.message);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {errorComp && <div className={styles.error}>{errorComp}</div>}

      {resetDone && (
        <div className={styles.success}>
          {getSuccessComponents(t("Signin.resetPasswordSuccessMessage"))}
        </div>
      )}

      <TextField
        name="email"
        disabled={!!mfaToken}
        label={t("Signin.Email address")}
        placeholder="johnsmith@gmail.com"
        variant="outlined"
        value={account.email}
        error={!!(formErrors.email && touched.email)}
        helperText={touched.email && formErrors.email}
        onChange={handleChangeField}
        onBlur={handleBlurField}
      />

      {mfaToken ? (
        <>
          <TextField
            inputRef={codeRef}
            name="code"
            className={styles.codeInput}
            label={t("Signin.Code")}
            type="number"
            variant="outlined"
            placeholder={t("Signin.Enter code")}
            value={account.code}
            error={!!(formErrors.code && touched.code)}
            helperText={touched.code && formErrors.code}
            onChange={handleChangeField}
            onBlur={handleBlurField}
          />

          <button className={styles.button} type="submit">
            {t("Signin.Submit Code")}
          </button>

          <button
            className={classNames(styles.link, styles.backButton)}
            type="button"
            onClick={handleBack}
          >
            <ArrowLeft />
            {t("Common.Back")}
          </button>
        </>
      ) : (
        <>
          <PasswordInput
            name="password"
            label={t("Signin.Password")}
            type="password"
            autoComplete="current-password"
            variant="outlined"
            placeholder={t("Signin.Enter your password")}
            value={account.password}
            error={!!(formErrors.password && touched.password)}
            helperText={touched.password && formErrors.password}
            onChange={handleChangeField}
            onBlur={handleBlurField}
          />

          <div className={styles.forgotLink}>
            <Link className={styles.link} to={routes.public.forgot.path()}>
              {t("Signin.forgotPassword")}
            </Link>
          </div>

          <button className={styles.button} type="submit">
            {t("Signin.emailSignInButton")}
          </button>
        </>
      )}
    </form>
  );
}
